'use client';

import { useMemo, useState } from 'react';
import Link from 'next/link';
import { MagnifyingGlassIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { EntityCard, OperationalState, OperationalStatus } from '@/app/Components/OperationalPage';
import { buttonClassName } from '@/app/Components/buttonStyles';
import { normalizeRoomSearchText } from '@/app/lib/rooms/normalize';
import type { DcsRoomSummary } from '@/app/lib/rooms/types';

const PAGE_SIZE = 36;

export default function RoomDirectory({ rooms }: { rooms: DcsRoomSummary[] }) {
  const [query, setQuery] = useState('');
  const [campus, setCampus] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const campuses = useMemo(
    () => [...new Set(rooms.map((room) => room.campus).filter((value): value is string => Boolean(value)))].sort(),
    [rooms],
  );
  const filtered = useMemo(() => {
    const normalized = normalizeRoomSearchText(query);
    return rooms.filter((room) => {
      if (campus && room.campus !== campus) return false;
      if (!normalized) return true;
      return [room.id, room.buildingName, room.campus, room.roomStyle, room.system]
        .filter(Boolean)
        .some((value) => normalizeRoomSearchText(value!).includes(normalized));
    });
  }, [campus, query, rooms]);
  const visible = filtered.slice(0, visibleCount);

  return (
    <>
      <section className="operational-section" aria-labelledby="room-search-heading">
        <div className="operational-section-header">
          <div>
            <h2 id="room-search-heading" className="operational-section-title">Search classrooms</h2>
            <p className="operational-section-description">Search by room ID, building, campus, room style, or classroom system.</p>
          </div>
          <OperationalStatus tone="info">{filtered.length} rooms</OperationalStatus>
        </div>
        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_15rem]">
          <label className="relative block">
            <span className="sr-only">Search classrooms</span>
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[var(--ss-text-muted)]" aria-hidden="true" />
            <input
              className="ss-field pl-10"
              value={query}
              onChange={(event) => { setQuery(event.target.value); setVisibleCount(PAGE_SIZE); }}
              placeholder="Try BE 111, Beck Hall, or Flip-Top"
            />
          </label>
          <label>
            <span className="sr-only">Filter by campus</span>
            <select
              className="ss-field"
              value={campus}
              onChange={(event) => { setCampus(event.target.value); setVisibleCount(PAGE_SIZE); }}
            >
              <option value="">All campuses</option>
              {campuses.map((entry) => <option key={entry}>{entry}</option>)}
            </select>
          </label>
        </div>
      </section>

      {visible.length ? (
        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" aria-label="Classroom results" data-audit-ready="room-directory-populated">
          {visible.map((room) => (
            <EntityCard
              key={room.id}
              eyebrow={room.campus ?? 'Rutgers classroom'}
              title={<Link className="underline decoration-[var(--ss-border-strong)] underline-offset-4 hover:text-[var(--ss-brand)]" href={room.href}>{room.id}</Link>}
              metadata={[room.buildingName, room.roomStyle].filter(Boolean).join(' · ')}
              status={room.capacity ? <OperationalStatus tone="neutral">{room.capacity} seats</OperationalStatus> : undefined}
              action={<Link href={room.href} className={buttonClassName({ variant: 'secondary', size: 'sm' })}><PhotoIcon className="h-4 w-4" aria-hidden="true" />See room details</Link>}
            >
              {room.system ?? 'Technology details are not listed by DCS.'}
            </EntityCard>
          ))}
        </section>
      ) : (
        <OperationalState kind="empty" title="No classrooms match" description="Try a room ID such as BE 111, a building name, or clear the campus filter." />
      )}

      {visible.length < filtered.length ? (
        <div className="flex justify-center">
          <button type="button" onClick={() => setVisibleCount((count) => count + PAGE_SIZE)} className={buttonClassName({ variant: 'secondary' })}>
            Show more classrooms
          </button>
        </div>
      ) : null}
    </>
  );
}
